import { useState, useMemo, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { message } from 'antd';

import { articleType, articleStatusType, articleContentType } from 'common/constants/content';

import { MUTATION_CREATE_ARTICLE, MUTATION_UPDATE_ARTICLE, QUERY_ARTICLE_DETAIL } from './queries';

const MAX_NUMBER = 99;
const BASE_ID_NUM = 1000;

const useArticle = () => {
  const params = useParams();
  const { type, index } = params;
  const history = useHistory();
  const id = params?.id ? decodeURIComponent(params?.id) : null;

  const [data, setData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currStatus, setCurrStatus] = useState();

  const [queryArticleContent, { loading }] = useLazyQuery(QUERY_ARTICLE_DETAIL, {
    fetchPolicy: 'network-only',
    variables: {
      id,
    },
    onCompleted: res => {
      if (res.article?.article) {
        const { status, articleData } = res.article.article;

        setData(
          articleData.map(({ index, type, content, title }, idx) => ({
            index,
            type,
            content,
            title,
            id: BASE_ID_NUM + idx,
          })),
        );
        setCurrStatus(status);
      }
    },
  });

  const [createArticle, { loading: createLoading }] = useMutation(MUTATION_CREATE_ARTICLE, {
    fetchPolicy: 'network-only',
    onCompleted: res => {
      if (res.createArticle.article) {
        message.success('create article success');

        history.goBack();
      }
    },
  });

  const [updateArticle, { loading: updateLoading }] = useMutation(MUTATION_UPDATE_ARTICLE, {
    fetchPolicy: 'network-only',
    onCompleted: res => {
      if (res.updateArticle.article) {
        message.success('update article success');

        history.goBack();
      }
    },
  });

  const title = useMemo(() => {
    const result = data.filter(item => item?.type === articleContentType.TITLE);

    const { content } = result?.[0] ?? {};

    return content;
  }, [data]);

  const articleTypeIndex = Object.values(articleType).findIndex(item => item === type) + 1;

  const handleSortIdx = items =>
    items.map((item, idx) => {
      const prevItems = items.slice(0, idx);
      const sameItems = prevItems.filter(_ => _.type === item.type);

      if (idx === 0 || !sameItems.length) {
        item.index = 1;
      } else {
        item.index = sameItems[sameItems.length - 1]?.index + 1;
      }

      return item;
    });

  const addContent = type => {
    const sameContent = data.filter(item => item.type === type) ?? [];
    const lastIdx = sameContent[sameContent.length - 1]?.index ?? 0;

    if (lastIdx === MAX_NUMBER || !Object.values(articleContentType).includes(type)) {
      return;
    }

    setActiveIndex(data.length);
    setData(prevData => [
      ...prevData,
      {
        type,
        index: lastIdx + 1,
        content: '',
        id: prevData[prevData.length - 1]?.id + 1,
      },
    ]);
  };

  const removeContent = () => {
    setData(prevData => handleSortIdx(prevData.filter((_, idx) => activeIndex !== idx)));
    setActiveIndex(null);
  };

  const setContent = data => {
    setData(data);
  };

  const handleSetActiveIdx = idx => {
    setActiveIndex(idx);
  };

  const handleSortClick = newSort => {
    if (
      newSort?.every((item, idx) =>
        idx === 0 ? item === 0 : newSort[idx] - newSort[idx - 1] === 1,
      )
    ) {
      return;
    }

    setData(prevData => {
      const result = [];

      newSort?.forEach(item => result.push(prevData[item]));

      return handleSortIdx(result);
    });

    setTimeout(() => {
      handleSetActiveIdx(newSort?.indexOf(activeIndex));
    }, 100);
  };

  const handleValueChange = (id, value, title) => {
    setData(prevData =>
      prevData.map(item => ({
        ...item,
        content: item.id === id ? value : item.content,
        title: item.id === id && title ? title : null,
      })),
    );
  };

  const handleSaveArticle = status => {
    const params = {
      type,
      status,
      articleData: data?.map(({ index, type, content, title }) => ({
        index,
        type,
        content,
        title,
      })),
    };

    if (id) {
      updateArticle({
        variables: {
          input: {
            id,
            ...params,
          },
        },
      });
    } else {
      createArticle({
        variables: {
          input: params,
        },
      });
    }
  };

  useEffect(() => {
    if (id) {
      queryArticleContent();
    } else {
      setCurrStatus(articleStatusType.NEW);
      setData([
        {
          type: articleContentType.TITLE,
          index: 1,
          content: '',
          id: BASE_ID_NUM,
        },
      ]);
    }
  }, []);

  return {
    type,
    index,
    title,
    data,
    status: currStatus,
    loading,
    btnLoading: createLoading || updateLoading,
    activeIndex,
    articleTypeIndex,
    addContent,
    setContent,
    removeContent,
    handleSetActiveIdx,
    handleValueChange,
    handleSortClick,
    handleSaveArticle,
  };
};

export default useArticle;
